/**
 * Products sold per UNIT in fixed multiples — "AI Visa Tutor, 99.00 USD less
 * 50.00 per license, in multiples of 50" (owner, 2026-10-16).
 *
 * ⚠️ **ONE COPY FOR THREE GRIDS.** The deal, quote and invoice line editors all
 * read these rules. Built generically: nothing here knows any product by name,
 * and nothing parses a product name. Behaviour comes only from the product's
 * `quantityStep` and `unitLabel` columns.
 *
 * ⛔ **NOTHING HERE FETCHES OR WRITES. It decides.** The server is the real
 * boundary: a line whose quantity is not a multiple of the step is a 422
 * `QUANTITY_NOT_MULTIPLE` on `quantity`. `quantityStepError` only says it first.
 *
 * ⚠️ **MONEY STAYS A DECIMAL STRING.** Every figure below is BigInt arithmetic
 * on scaled cents; no `Number()` ever touches an amount.
 *
 * ⚠️ **`recommendedDiscountAmount` IS SEEDED PER LINE TODAY.**
 * `recommendedDiscountSeed` (`lib/line-presets.ts`) copies it unscaled into the
 * line's `discountAmount`, which discounts the WHOLE line (gross − amount). A
 * per-unit discount has to be multiplied by the quantity first —
 * `perUnitDiscountTotal` — before it can be seeded as a line discount.
 */

/** The fields of a product these rules read. Absent = an older API. */
export interface ProductUnitFields {
  quantityStep?: number | null;
  unitLabel?: string | null;
}

/** A whole, non-negative count as typed, or `null` when it is not one. */
function wholeCount(value: string | number): bigint | null {
  const text = typeof value === 'number' ? String(value) : value.trim();
  if (!/^\d+$/.test(text)) return null;
  return BigInt(text);
}

/** A step worth enforcing: a positive whole number, else `null`. */
function stepOf(product: ProductUnitFields | null | undefined): number | null {
  const step = product?.quantityStep;
  if (typeof step !== 'number' || !Number.isInteger(step) || step < 1) return null;
  return step;
}

/** A decimal string as scaled cents, or `null` when not a decimal of ≤2 places. */
function cents(value: string): bigint | null {
  const trimmed = value.trim();
  const match = /^(-?)(\d+)(?:\.(\d{1,2}))?$/.exec(trimmed);
  if (!match) return null;
  const [, sign = '', whole = '0', fraction = ''] = match;
  const units = BigInt(whole) * 100n + BigInt(fraction.padEnd(2, '0'));
  return sign === '-' ? -units : units;
}

/** Scaled cents back to a plain two-place string, no grouping. */
function formatCents(units: bigint): string {
  const negative = units < 0n;
  const digits = (negative ? -units : units).toString().padStart(3, '0');
  const body = `${digits.slice(0, -2)}.${digits.slice(-2)}`;
  return negative ? `-${body}` : body;
}

/** True when a typed quantity is exactly one. */
function isOne(qty: string | number): boolean {
  if (typeof qty === 'number') return qty === 1;
  return /^0*1(\.0*)?$/.test(qty.trim());
}

/**
 * The unit label for a quantity: `'license'` → `'licenses'`, unless the
 * quantity is exactly 1.
 */
export function pluralUnit(label: string, qty: string | number): string {
  const word = label.trim();
  if (word === '' || isOne(qty)) return word;
  if (/(s|x|z|ch|sh)$/i.test(word)) return `${word}es`;
  if (/[^aeiou]y$/i.test(word)) return `${word.slice(0, -1)}ies`;
  return `${word}s`;
}

/**
 * The refusal for a quantity that is not a multiple of the product's step, or
 * `null` when it is, when the product has no step, or when the quantity is not
 * a whole number at all (that is another rule's refusal, not this one's).
 */
export function quantityStepError(
  product: ProductUnitFields | null | undefined,
  quantity: string,
): string | null {
  const step = stepOf(product);
  if (step === null) return null;
  const qty = wholeCount(quantity);
  if (qty === null || qty === 0n) return null;
  if (qty % BigInt(step) === 0n) return null;
  const label = product?.unitLabel?.trim() ?? '';
  const unit = label === '' ? '' : ` ${pluralUnit(label, step)}`;
  return `Sold in multiples of ${step}${unit}.`;
}

/** The quantity a new line selling this product starts at. */
export function defaultQuantityFor(product: ProductUnitFields | null | undefined): string {
  const step = stepOf(product);
  return step === null ? '1' : String(step);
}

/**
 * A per-unit discount across a whole line: `perUnitDiscount × quantity`, to two
 * places. `null` when there is no discount, or when either operand is not a
 * value this can multiply exactly (never a guess, never `0`).
 */
export function perUnitDiscountTotal(
  perUnitDiscount: string | null,
  quantity: string,
): string | null {
  if (perUnitDiscount === null) return null;
  const unit = cents(perUnitDiscount);
  const qty = wholeCount(quantity);
  if (unit === null || qty === null) return null;
  return formatCents(unit * qty);
}

export interface AutoLineDescriptionArgs {
  productName: string;
  quantity: string;
  unitLabel: string;
  unitPrice: string | null;
  discountPerUnit: string | null;
  currency: string;
}

/**
 * The description a per-unit line starts with, e.g.
 *
 *     SEED AI Visa Tutor: 50 licenses
 *     Standard price: 99.00 USD per license
 *     Discounted price: 49.00 USD per license
 *
 * The price lines are omitted when the line has no price; the discounted line
 * is omitted when there is no (or a zero) per-unit discount. `productName` is
 * used exactly as given.
 */
export function autoLineDescription(args: AutoLineDescriptionArgs): string {
  const label = args.unitLabel.trim();
  const quantity = args.quantity.trim();
  const currency = args.currency.trim();
  const lines = [`${args.productName.trim()}: ${quantity} ${pluralUnit(label, quantity)}`];

  const price = args.unitPrice === null ? null : cents(args.unitPrice);
  if (price !== null) {
    lines.push(`Standard price: ${formatCents(price)} ${currency} per ${label}`);
    const discount = args.discountPerUnit === null ? null : cents(args.discountPerUnit);
    if (discount !== null && discount !== 0n) {
      lines.push(`Discounted price: ${formatCents(price - discount)} ${currency} per ${label}`);
    }
  }
  return lines.join('\n');
}

/**
 * Whether a line's description is still exactly the auto text for `args`, so a
 * grid may regenerate it when the quantity changes. Compared after trimming and
 * normalising line endings. An empty description is NOT the auto text: a
 * description somebody cleared stays cleared.
 */
export function isAutoDescription(text: string, args: AutoLineDescriptionArgs): boolean {
  const normalise = (value: string) => value.replace(/\r\n?/g, '\n').trim();
  const current = normalise(text);
  if (current === '') return false;
  return current === normalise(autoLineDescription(args));
}
